import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAudit } from '../context/AuditContext';
import { PageFrame } from '../components/PageFrame';
import { SectionCard } from '../components/SectionCard';
import { NavigationControls } from '../components/NavigationControls';
import { TooltipWrapper, TOOLTIP_CONTENT } from '../components/TooltipWrapper';
import { TrendingUp, ArrowUpRight, User, Quote } from 'lucide-react';
import { Button } from '../components/ui/button';
import { motion } from 'framer-motion';

export default function SummaryPage() {
  const navigate = useNavigate();
  const { auditData, getOverallScore, getRangeLabel, getTotalRoiAnnual } = useAudit();

  const overallScore = getOverallScore();
  const rangeLabel = getRangeLabel(overallScore);
  const totalRoi = getTotalRoiAnnual();

  const roadmapSteps = [
    { label: 'Mini-Audit', status: 'complete', description: 'You are here!' },
    { label: 'Full Audit', status: 'next', description: 'Deep ROI modeling' },
    { label: '90-Day Roadmap', status: 'upcoming', description: 'Prioritized action plan' },
    { label: 'Implementation', status: 'upcoming', description: 'Launch & scale' }
  ];

  return (
    <PageFrame pageNumber={9} backgroundStyle="dark">
      <div className="min-h-screen py-12">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto space-y-8">
            {/* Header */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center mb-12"
            >
              <h1 className="text-4xl sm:text-5xl font-extrabold uppercase text-white mb-4" data-testid="summary-title">
                Your Results Are Just the Beginning
              </h1>
              <p className="text-xl text-white/80">
                Based on your responses, here's what we uncovered:
              </p>
            </motion.div>

            {/* Score Summary Card */}
            <div className="bg-white rounded-lg shadow-lg p-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="text-sm text-muted-foreground">Your Overall Score</div>
                    <TooltipWrapper content={TOOLTIP_CONTENT.overallScore} iconSize={14} />
                  </div>
                  <div className="text-6xl font-bold text-primary mb-2" data-testid="summary-overall-score">
                    {overallScore}
                  </div>
                  <div className="inline-flex items-center gap-2 bg-accent/20 text-accent-foreground px-4 py-2 rounded-full font-semibold">
                    <TrendingUp className="h-5 w-5" />
                    {rangeLabel}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground mb-2">Estimated Annual ROI Potential</div>
                  <div className="text-5xl font-bold text-secondary mb-2" data-testid="summary-roi-potential">
                    ${totalRoi.toLocaleString()}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Based on the processes you identified
                  </p>
                </div>
              </div>

              <div className="grid md:grid-cols-4 gap-4 mt-8 pt-8 border-t border-muted">
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">{auditData.sections.operations.total}</div>
                  <div className="text-xs text-muted-foreground mt-1">Operations</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">{auditData.sections.customer.total}</div>
                  <div className="text-xs text-muted-foreground mt-1">Customer</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">{auditData.sections.marketing.total}</div>
                  <div className="text-xs text-muted-foreground mt-1">Marketing</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">{auditData.sections.team.total}</div>
                  <div className="text-xs text-muted-foreground mt-1">Team</div>
                </div>
              </div>
            </div>

            {/* Full Audit Info */}
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h2 className="text-2xl font-bold text-secondary mb-4">What's Next: Full AI Opportunity Audit™</h2>
              <p className="text-foreground mb-6">
                This Mini-Audit gave you directional insights. Our Full Audit includes:
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-primary mt-2"></div>
                  <div><strong className="text-secondary">ROI modeling</strong> with 6-12 month projections</div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-primary mt-2"></div>
                  <div><strong className="text-secondary">Prioritized 90-day roadmap</strong> with quick wins identified</div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-primary mt-2"></div>
                  <div><strong className="text-secondary">Implementation support</strong> and vendor recommendations</div>
                </li>
              </ul>

              {/* Roadmap Graphic */}
              <div className="flex items-center justify-between gap-2 mb-6">
                {roadmapSteps.map((step, index) => (
                  <div key={index} className="flex-1">
                    <div className={`text-center p-4 rounded-lg ${
                      step.status === 'complete' ? 'bg-accent/20 border-2 border-accent' :
                      step.status === 'next' ? 'bg-primary/10 border-2 border-primary' :
                      'bg-muted border-2 border-muted'
                    }`}>
                      <div className={`text-sm font-bold mb-1 ${
                        step.status === 'complete' ? 'text-accent-foreground' :
                        step.status === 'next' ? 'text-primary' :
                        'text-muted-foreground'
                      }`}>
                        {step.label}
                      </div>
                      <div className="text-xs text-muted-foreground">{step.description}</div>
                    </div>
                    {index < roadmapSteps.length - 1 && (
                      <div className="text-center mt-2">
                        <ArrowUpRight className="h-4 w-4 text-muted-foreground mx-auto" />
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* CTA Button */}
              <Button
                size="lg"
                className="w-full bg-accent hover:bg-accent/90 text-accent-foreground font-bold py-6 text-lg gap-3 group"
                onClick={() => window.open('https://trilogyoneconsulting.com/full-audit', '_blank')}
                data-testid="book-full-audit-button"
              >
                Book Your Full AI Opportunity Audit™ (50% Off This Week)
                <ArrowUpRight className="h-5 w-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </Button>
            </div>

            {/* Testimonial */}
            <div className="bg-white border-2 border-primary rounded-lg p-6">
              <Quote className="h-8 w-8 text-primary mb-4" />
              <p className="text-foreground italic mb-4">
                "We uncovered $60K in annual savings within two weeks of working with Trilogy One. The roadmap was clear, actionable, and perfectly scoped for our team."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <div className="font-semibold text-secondary">Small Business Owner</div>
                  <div className="text-sm text-muted-foreground">Delaware</div>
                </div>
              </div>
            </div>

            {/* Navigation */}
            <div className="pt-4">
              <NavigationControls currentPage={9} />
            </div>
          </div>
        </div>
      </div>
    </PageFrame>
  );
}